import React from 'react'
import { IconButton } from '@mui/material'
import assignUser from 'assets/icons/icon-assign-user.svg'
import unflagged from 'assets/icons/icon-flag--unflagged.svg'
import flagged from 'assets/icons/icon-flag.svg'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { Icon as IconIcon } from 'components'
import { iconsTheme} from 'theme-exports'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling';

interface BaseIconProps {
    src: string
    height?: number
    width?: number
    alt?: string
    className?: string
    id?: string
}

export const Icon = ({
    src,
    height = 20,
    width = 20,
    alt = 'icon',
    id = '',
}: BaseIconProps) => (
    <img
        height={height}
        width={width}
        src={src}
        alt={alt}
        {...(id && { id })}
    />
)

interface IconProps {
    onClick?: () => void
    assignEnabled?: boolean
    className?: string
    rowIndex?: number
    testId?: string
    disableRipple?: boolean
    selectedTabFilter?: string | number
}

interface FlagIconProps extends IconProps {
    isFlagged: boolean
}

export const FlagIcon = ({
    isFlagged,
    onClick,
    className = '',
    rowIndex,
    testId,
}: FlagIconProps) => {
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    return (
        <IconButton
            onClick={onClick}
            className={className}
            disabled={isReadOnly}
            size="large">
            {iconsTheme.flagged ? (
                <IconIcon
                    className={`${
                        isFlagged ? iconsTheme.flagged : iconsTheme.unflagged
                    } `}
                    id={`${testId}-flag-icon-${rowIndex}`}
                />
            ) : (
                <Icon
                    src={isFlagged ? flagged : unflagged}
                    alt={isFlagged ? 'flagged' : 'unflagged'}
                    id={`${testId}-flag-icon-${rowIndex}`}
                />
            )}
        </IconButton>
    );
}

export const AssignUserIcon = ({
    onClick,
    assignEnabled = true,
    className = '',
    rowIndex,
    testId,
    disableRipple = false,
    selectedTabFilter = '',
}: IconProps) => {
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    return (
        <IconButton
            onClick={onClick}
            className={className}
            disabled={!assignEnabled || isReadOnly}
            disableRipple={disableRipple}
            size="large"
        >
            {iconsTheme.assign ? (
                <IconIcon
                    className={
                        assignEnabled || selectedTabFilter === 'new_cases'
                            ? iconsTheme.assign
                            : iconsTheme.cross
                    }
                    id={`${testId}-assign-user-icon-${rowIndex}`}
                />
            ) : (
                <Icon
                    src={assignUser}
                    alt="assign user icon."
                    id={`${testId}-assign-user-icon-${rowIndex}`}
                />
            )}
        </IconButton>
    )
}

interface UploadIconProps extends IconProps {
    disabled?: boolean
    testId: string
}

export const UploadIcon = ({
    onClick = () => {},
    disabled = false,
    testId,
}: UploadIconProps) => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    const isReadOnly = user?.is_read_only

    return (
        <IconIcon
            onClick={disabled || isReadOnly ? () => {} : () => onClick()}
            className={`${
                disabled ? 'icon-upload-disabled' : 'icon-upload'
            } ${'emp-icons-uploadIcon-container'} ${
                disabled && 'emp-icons-uploadIcon-disabledIcon'
            } ${isReadOnly && 'emp-icons-uploadIcon-disabledColor'}`}
            id={testId}
            iconColor={clientTheme.icons.uploadIcon}
        />
    )
}

interface DocumentMagnifyingGlassIconProps {
    iconColor?: string
    className?: string
    id?: string
}

export const DocumentMagnifyingGlassIcon = ({
    iconColor = '#333333',
    className = '',
    id = '',
}: DocumentMagnifyingGlassIconProps) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.75}
            stroke={iconColor}
            className={className}
            id={id}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
            />
        </svg>
    )
}

export default { FlagIcon }
